import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Auftrag, AuftragPosition, Partner, Artikel } from '../types';
import KatalogSuchePanel from './KatalogSuchePanel';
import { downloadDatei } from '../lib/download';
import BelegDokumentView from './BelegDokumentView';
import AktivitaetenBlock from './AktivitaetenBlock';
import { RegieberichtKarte, leseRolleAusSpeicher } from './RegieberichtForm';
import { useAktiveModule } from '../hooks/useAktiveModule';
import './AuftragForm.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// Status, in denen Kopf-/Positionsbearbeitung fachlich erlaubt ist (AP 3.1).
// Server-seitig verbindlich geprüft in auftrag_api._pruefe_kopf_positions_bearbeitbar
// (branchenabhängig: die ersten zwei Status je Auftragstyp-Statuskette —
// PRODUKTION/HANDEL: Angebot/Bestätigt, DIENSTLEISTUNG/PROJEKT: Angebot/
// Beauftragt). Diese Konstante ist die Vereinigung aller vier Ketten und
// dient nur der UI-Vorabblendung; die Liste kennt den Auftragstyp nicht.
export const AUFTRAG_BEARBEITBARE_STATUS = ['Angebot', 'Bestätigt', 'Beauftragt'];

/**
 * Belegtypen der Dokumentansicht in der Reihenfolge des Belegflusses.
 * `wert` ist zugleich das Pfadsegment der Ansicht (?typ=) und des
 * PDF-Endpunkts (/api/dokumente/auftraege/{id}/{wert}/pdf); `kuerzel` ist das
 * Praefix des Dateinamens und greift nur als Fallback — den verbindlichen
 * Namen liefert der Server im Content-Disposition-Header.
 */
const DOKUMENT_TYPEN = [
  { wert: 'angebot', label: 'Angebot', kuerzel: 'AN' },
  { wert: 'auftragsbestaetigung', label: 'Auftragsbestätigung', kuerzel: 'AB' },
  { wert: 'lieferschein', label: 'Lieferschein', kuerzel: 'LS' },
] as const;

type DokumentTyp = (typeof DOKUMENT_TYPEN)[number]['wert'];

interface AuftragFormProps {
  editingAuftrag?: Auftrag | null;
  onSuccess: () => void;
  onCancel: () => void;
}

interface NeueZeile {
  artikel_id: string;
  menge: string;
  preis_einzeln: string;
  rabatt_prozent: string;
}

const leereZeile = (): NeueZeile => ({ artikel_id: '', menge: '1', preis_einzeln: '', rabatt_prozent: '' });

const formatPrice = (price?: number | null) => (price === null || price === undefined ? '-' : `${price.toFixed(2)} €`);

/** Metallzuschlag-Vorschlag aus find_best_price (AP 3.5), je Artikel gecacht. */
interface MetallInfo {
  zuschlag: number;
  notierung_datum: string | null;
}

/** ISO-Datum -> TT.MM.JJJJ (leer/unlesbar -> null). */
const datumDe = (iso?: string | null): string | null => {
  if (!iso) return null;
  const teile = String(iso).slice(0, 10).split('-');
  if (teile.length !== 3) return null;
  return `${teile[2]}.${teile[1]}.${teile[0]}`;
};

/**
 * Zeilen-Hinweis „zzgl. Kupferzuschlag …" — der Zuschlag steckt bewusst NICHT
 * im Positionspreis (Hohlpreis-Modell, Spec §8.3), muss auf der Maske aber
 * sichtbar sein: sonst wundert sich der Bearbeiter über die Differenz zwischen
 * Positionssumme und Belegnetto.
 */
const zuschlagHinweis = (
  zuschlag?: number | null,
  einheit?: string | null,
  notierungDatum?: string | null,
): string | null => {
  if (!zuschlag) return null;
  const datum = datumDe(notierungDatum);
  const je = einheit ? `€/${einheit}` : '€';
  return `zzgl. Kupferzuschlag ${zuschlag.toFixed(5).replace('.', ',')} ${je}`
    + (datum ? `, Notiz vom ${datum}` : '');
};

const AuftragForm: React.FC<AuftragFormProps> = ({ editingAuftrag, onSuccess, onCancel }) => {
  const isEdit = !!editingAuftrag;
  const userRolle = leseRolleAusSpeicher();
  // Wie AuftragsAkte.tsx: die Karte ruft /api/regieberichte, das einsatz-
  // gated ist. Ohne dieses Gate zeigt AuftragForm bei Tenants ohne das Modul
  // eine rote Fehlerkarte statt einfach nichts.
  const aktiveModule = useAktiveModule();
  const regieberichtModulAktiv = aktiveModule.size === 0 || aktiveModule.has('einsatz');

  // Reaktiver Status statt einmaligem Prop-Snapshot: nach jeder 409-Antwort
  // (Auftrag hat die Angebotsphase inzwischen verlassen — z.B. Statuswechsel
  // durch einen anderen Nutzer/Tab) wird der Auftrag neu geladen und
  // statusBearbeitbar hier neu bewertet, statt dauerhaft den Stand von der
  // Öffnung der Maske zu zeigen.
  const [auftragStatus, setAuftragStatus] = useState<string | undefined>(editingAuftrag?.status);
  const statusBearbeitbar = !isEdit || AUFTRAG_BEARBEITBARE_STATUS.includes(auftragStatus || '');

  // Wird bei jedem Reload (409-Fall) hochgezählt und fließt in die Input-Keys
  // der Positions-Zeilen ein — erzwingt einen Remount der unkontrollierten
  // Felder, damit ein vom Server abgelehnter/verworfener Wert nicht als
  // vermeintlich gespeichert stehen bleibt.
  const [resetGeneration, setResetGeneration] = useState(0);
  // Kurze Zeilen-Fehlermeldungen für lokal ungültige Eingaben (Menge/Preis/
  // Rabatt außerhalb des erlaubten Bereichs), Key: "<position_id>-<feld>".
  const [zeilenFehler, setZeilenFehler] = useState<Record<string, string>>({});

  const zeileFehlerSetzen = (feldKey: string, meldung: string | null) => {
    setZeilenFehler(prev => {
      if (meldung === null) {
        if (!(feldKey in prev)) return prev;
        const next = { ...prev };
        delete next[feldKey];
        return next;
      }
      return { ...prev, [feldKey]: meldung };
    });
  };

  const [kundeId, setKundeId] = useState<string>(
    editingAuftrag ? String(editingAuftrag.kunde_id) : ''
  );
  const [liefertermin, setLiefertermin] = useState<string>(
    editingAuftrag?.liefertermin ? editingAuftrag.liefertermin.slice(0, 10) : ''
  );
  const [prioritaet, setPrioritaet] = useState<string>(String(editingAuftrag?.prioritaet ?? 5));
  const [notizen, setNotizen] = useState<string>(editingAuftrag?.notizen || '');
  const [kopftext, setKopftext] = useState<string>(editingAuftrag?.kopftext || '');
  const [fusstext, setFusstext] = useState<string>(editingAuftrag?.fusstext || '');
  const [gesamtrabatt, setGesamtrabatt] = useState<string>(
    editingAuftrag?.gesamtrabatt_prozent != null ? String(editingAuftrag.gesamtrabatt_prozent) : ''
  );

  const [kunden, setKunden] = useState<Partner[]>([]);
  const [artikelListe, setArtikelListe] = useState<Artikel[]>([]);

  // Neuanlage: Positionen werden lokal gehalten und mit dem POST-Request
  // (Kopf + Positions-Array) in einem Request angelegt.
  const [neueZeilen, setNeueZeilen] = useState<NeueZeile[]>([leereZeile()]);

  // Bearbeitung: bestehende Positionen, jede Änderung wird sofort über die
  // vorhandenen Positions-CRUD-Endpoints persistiert (Muster BestellungForm).
  const [positionen, setPositionen] = useState<AuftragPosition[]>(editingAuftrag?.positionen || []);
  const [zeileWirdGespeichert, setZeileWirdGespeichert] = useState<number | null>(null);
  const [neuePosition, setNeuePosition] = useState<NeueZeile>(leereZeile());

  // Preisvorschlag aus find_best_price (AP 2.2), je Artikel gecacht — nur
  // Vorschlag/Platzhalter, das Feld bleibt frei überschreibbar.
  const [preisVorschlaege, setPreisVorschlaege] = useState<Record<number, number>>({});

  // Metallzuschlag desselben Aufrufs (AP 3.5): in der Neuanlage gibt es noch
  // keinen Positions-Snapshot, deshalb ist der Vorschlag die einzige Quelle
  // für die Vorschau. Beim Anlegen friert der Server ihn dann ein.
  const [metallInfos, setMetallInfos] = useState<Record<number, MetallInfo>>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pdfLaedt, setPdfLaedt] = useState(false);
  const [ansichtModus, setAnsichtModus] = useState<'maske' | 'dokument'>('maske');
  const [dokumentTyp, setDokumentTyp] = useState<DokumentTyp>('angebot');

  const ladeStammdaten = useCallback(() => {
    Promise.all([
      axios.get<Partner[]>(`${API_URL}/api/partners`),
      axios.get<Artikel[]>(`${API_URL}/api/artikel`),
    ]).then(([pRes, aRes]) => {
      setKunden(pRes.data.filter(p => p.typ === 'Kunde' || p.typ === 'Beides'));
      setArtikelListe(aRes.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    ladeStammdaten();
  }, [ladeStammdaten]);

  // Auftrag frisch vom Server laden — nach einer 409-Antwort (Auftrag hat die
  // Angebotsphase inzwischen verlassen, z.B. Statuswechsel in einem anderen
  // Tab) ist der lokale Stand (Status, Positionen, Kopffelder) veraltet.
  // Reload synct alles auf den Server-Stand und lässt statusBearbeitbar neu
  // bewerten; resetGeneration erzwingt zusätzlich einen Remount der
  // unkontrollierten Positions-Inputs, damit ein abgelehnter/eingetippter
  // Wert nicht fälschlich als gespeichert stehen bleibt.
  const reloadAuftrag = useCallback(async () => {
    if (!editingAuftrag?.auftrag_id) return;
    try {
      const res = await axios.get<Auftrag>(`${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}`);
      const frisch = res.data;
      setAuftragStatus(frisch.status);
      setPositionen(frisch.positionen || []);
      setLiefertermin(frisch.liefertermin ? frisch.liefertermin.slice(0, 10) : '');
      setPrioritaet(String(frisch.prioritaet ?? 5));
      setNotizen(frisch.notizen || '');
      setKopftext(frisch.kopftext || '');
      setFusstext(frisch.fusstext || '');
      setGesamtrabatt(frisch.gesamtrabatt_prozent != null ? String(frisch.gesamtrabatt_prozent) : '');
      setZeilenFehler({});
      setResetGeneration(g => g + 1);
    } catch {
      // Reload selbst fehlgeschlagen (z.B. Netzwerk) — die Fehlermeldung aus
      // der ursprünglichen 409-Antwort bleibt sichtbar; der Nutzer kann über
      // "Abbrechen" die Maske verlassen.
    }
  }, [editingAuftrag]);

  // Gemeinsame Fehlerbehandlung für Kopf-/Positions-Schreiboperationen: bei
  // 409 (Bearbeitungs-Gate serverseitig ausgelöst) wird der Auftrag neu
  // geladen statt nur eine generische Banner-Meldung zu zeigen.
  const nachFehlerReagieren = async (err: any, fallbackMsg: string) => {
    if (err?.response?.status === 409) {
      await reloadAuftrag();
      setError(
        err.response?.data?.detail ||
        'Auftrag ist nicht mehr bearbeitbar — Ansicht wurde auf den aktuellen Stand aktualisiert.'
      );
    } else {
      setError(err?.response?.data?.detail || fallbackMsg);
    }
  };

  const artikelOf = (id: number) => artikelListe.find(a => a.artikel_id === id);
  const artikelName = (id: number) => artikelOf(id)?.bezeichnung ?? `#${id}`;

  // Preisvorschlag laden, sobald ein Artikel gewählt wird (überschreibt nie
  // einen bereits eingegebenen Preis, dient nur als Platzhalter/Default).
  const preisVorschlagLaden = useCallback(async (artikelId: number) => {
    if (preisVorschlaege[artikelId] !== undefined) return;
    try {
      const params = new URLSearchParams();
      params.append('artikel_id', String(artikelId));
      params.append('menge', '1');
      if (kundeId) params.append('partner_id', kundeId);
      const res = await axios.get(`${API_URL}/api/preiskonditionen/preis?${params}`);
      const preis = res.data?.preis_pro_einheit;
      if (typeof preis === 'number') {
        setPreisVorschlaege(prev => ({ ...prev, [artikelId]: preis }));
      }
      // preis_pro_einheit enthält den Metallzuschlag bewusst NICHT — er ist ein
      // getrennt auszuweisender Bestandteil und wird hier separat gemerkt.
      const zuschlag = res.data?.metallzuschlag;
      if (typeof zuschlag === 'number') {
        setMetallInfos(prev => ({
          ...prev,
          [artikelId]: { zuschlag, notierung_datum: res.data?.notierung_datum ?? null },
        }));
      }
    } catch {
      // Kein Preisvorschlag verfügbar (z.B. keine Kondition, kein preis_ek) — Feld bleibt frei editierbar.
    }
  }, [kundeId, preisVorschlaege]);

  const preisPlatzhalter = (artikelId: string): string => {
    const id = parseInt(artikelId);
    if (!id) return '0.00';
    const vorschlag = preisVorschlaege[id];
    if (vorschlag !== undefined) return vorschlag.toFixed(2);
    return artikelOf(id)?.preis_vk?.toFixed(2) ?? '0.00';
  };

  // ---- Steuersatz-gruppierte Summenvorschau (rein informativ) ----

  const steuersatzVon = (artikelId: string): number => artikelOf(parseInt(artikelId))?.steuersatz ?? 19;

  const zeilensumme = (menge: number, preis: number, rabatt: number) => menge * preis * (1 - rabatt / 100);

  // Metallzuschlag der Vorschau: `netto` bleibt der (ggf. gesamtrabattierte)
  // Positionsanteil, `zuschlag` wird getrennt geführt — auf ihn gibt es keinen
  // Rabatt (Spec §8.3), er geht aber in die MwSt-Bemessung seiner eigenen
  // Steuergruppe ein (§8.5). Genau so rechnet auch `beleg_arithmetik`.
  interface VorschauGruppe { netto: number; satz: number; zuschlag: number }

  const summenVorschauNeu = (): VorschauGruppe[] => {
    const gruppen: Record<string, VorschauGruppe> = {};
    for (const z of neueZeilen) {
      if (!z.artikel_id || !(parseFloat(z.menge) > 0)) continue;
      const menge = parseFloat(z.menge) || 0;
      const artikelId = parseInt(z.artikel_id);
      const preis = z.preis_einzeln ? parseFloat(z.preis_einzeln) : (preisVorschlaege[artikelId] ?? artikelOf(artikelId)?.preis_vk ?? 0);
      const rabatt = z.rabatt_prozent ? parseFloat(z.rabatt_prozent) : 0;
      const satz = steuersatzVon(z.artikel_id);
      const key = String(satz);
      if (!gruppen[key]) gruppen[key] = { netto: 0, satz, zuschlag: 0 };
      gruppen[key].netto += zeilensumme(menge, preis, rabatt);
      gruppen[key].zuschlag += menge * (metallInfos[artikelId]?.zuschlag ?? 0);
    }
    return Object.values(gruppen);
  };

  const summenVorschauEdit = (): VorschauGruppe[] => {
    const gesamtrabattFaktor = 1 - (parseFloat(gesamtrabatt) || 0) / 100;
    const gruppen: Record<string, VorschauGruppe> = {};
    for (const p of positionen) {
      const satz = p.steuersatz ?? 19;
      const key = String(satz);
      if (!gruppen[key]) gruppen[key] = { netto: 0, satz, zuschlag: 0 };
      gruppen[key].netto += (p.preis_gesamt || 0) * gesamtrabattFaktor;
      gruppen[key].zuschlag += (p.menge || 0) * (p.metallzuschlag_einzeln ?? 0);
    }
    return Object.values(gruppen);
  };

  const SummenVorschau: React.FC<{ gruppen: VorschauGruppe[] }> = ({ gruppen }) => {
    if (gruppen.length === 0) return null;
    const nettoGesamt = gruppen.reduce((s, g) => s + g.netto, 0);
    const zuschlagGesamt = gruppen.reduce((s, g) => s + g.zuschlag, 0);
    // MwSt je Gruppe INKLUSIVE Zuschlag — sonst wiche die Vorschau vom Beleg ab.
    const mwstGesamt = gruppen.reduce((s, g) => s + (g.netto + g.zuschlag) * (g.satz / 100), 0);
    return (
      <div className="summen-vorschau">
        {gruppen.map(g => (
          <div key={g.satz} className="summen-zeile">
            <span>Netto ({g.satz}% MwSt)</span>
            <span>{formatPrice(g.netto)}</span>
          </div>
        ))}
        {zuschlagGesamt > 0 && (
          <div className="summen-zeile">
            <span>+ Kupferzuschlag</span>
            <span>{formatPrice(zuschlagGesamt)}</span>
          </div>
        )}
        <div className="summen-zeile">
          <span>MwSt gesamt</span>
          <span>{formatPrice(mwstGesamt)}</span>
        </div>
        <div className="summen-zeile summen-zeile--total">
          <span>Brutto gesamt</span>
          <span>{formatPrice(nettoGesamt + zuschlagGesamt + mwstGesamt)}</span>
        </div>
      </div>
    );
  };

  // ---- Neuanlage: lokale Zeilen ----

  const zeileAendern = (index: number, feld: keyof NeueZeile, wert: string) => {
    setNeueZeilen(prev => prev.map((z, i) => (i === index ? { ...z, [feld]: wert } : z)));
    if (feld === 'artikel_id' && wert) {
      preisVorschlagLaden(parseInt(wert));
    }
  };

  const zeileHinzufuegen = () => setNeueZeilen(prev => [...prev, leereZeile()]);

  const zeileEntfernen = (index: number) => {
    setNeueZeilen(prev => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleSubmitNeu = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!kundeId) {
      setError('Kunde ist erforderlich');
      return;
    }
    const gueltigeZeilen = neueZeilen.filter(z => z.artikel_id && parseFloat(z.menge) > 0);
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/auftraege`, {
        kunde_id: parseInt(kundeId),
        liefertermin: liefertermin || undefined,
        prioritaet: prioritaet ? parseInt(prioritaet) : undefined,
        notizen: notizen || undefined,
        positionen: gueltigeZeilen.map(z => ({
          artikel_id: parseInt(z.artikel_id),
          menge: parseFloat(z.menge),
          preis_einzeln: z.preis_einzeln ? parseFloat(z.preis_einzeln) : undefined,
          rabatt_prozent: z.rabatt_prozent ? parseFloat(z.rabatt_prozent) : undefined,
        })),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Fehler beim Anlegen des Angebots');
    } finally {
      setSaving(false);
    }
  };

  // ---- Bearbeitung: Kopf ----

  const handleSubmitKopf = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingAuftrag) return;
    setError('');
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}`, {
        liefertermin: liefertermin || null,
        prioritaet: prioritaet ? parseInt(prioritaet) : undefined,
        notizen: notizen || null,
        kopftext: kopftext || null,
        fusstext: fusstext || null,
        gesamtrabatt_prozent: gesamtrabatt !== '' ? parseFloat(gesamtrabatt) : null,
      });
      onSuccess();
    } catch (err: any) {
      await nachFehlerReagieren(err, 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  // ---- Bearbeitung: Positionen (sofort persistiert) ----

  const positionHinzufuegen = async () => {
    if (!editingAuftrag || !neuePosition.artikel_id || !(parseFloat(neuePosition.menge) > 0)) return;
    setError('');
    setZeileWirdGespeichert(-1);
    try {
      const res = await axios.post<AuftragPosition>(
        `${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}/positionen`,
        {
          artikel_id: parseInt(neuePosition.artikel_id),
          menge: parseFloat(neuePosition.menge),
          preis_einzeln: neuePosition.preis_einzeln ? parseFloat(neuePosition.preis_einzeln) : undefined,
          rabatt_prozent: neuePosition.rabatt_prozent ? parseFloat(neuePosition.rabatt_prozent) : undefined,
        }
      );
      setPositionen(prev => [...prev, res.data]);
      setNeuePosition(leereZeile());
    } catch (err: any) {
      await nachFehlerReagieren(err, 'Fehler beim Hinzufügen der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const positionAendern = async (pos: AuftragPosition, menge: number, preisEinzeln: number, rabattProzent: number | null) => {
    if (!editingAuftrag) return;
    setError('');
    setZeileWirdGespeichert(pos.position_id!);
    try {
      const res = await axios.put<AuftragPosition>(
        `${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}/positionen/${pos.position_id}`,
        { menge, preis_einzeln: preisEinzeln, rabatt_prozent: rabattProzent }
      );
      setPositionen(prev => prev.map(p => (p.position_id === pos.position_id ? res.data : p)));
    } catch (err: any) {
      await nachFehlerReagieren(err, 'Fehler beim Aktualisieren der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const positionLoeschen = async (pos: AuftragPosition) => {
    if (!editingAuftrag) return;
    setError('');
    setZeileWirdGespeichert(pos.position_id!);
    try {
      await axios.delete(
        `${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}/positionen/${pos.position_id}`
      );
      setPositionen(prev => prev.filter(p => p.position_id !== pos.position_id));
    } catch (err: any) {
      await nachFehlerReagieren(err, 'Fehler beim Löschen der Position');
    } finally {
      setZeileWirdGespeichert(null);
    }
  };

  const dokument = DOKUMENT_TYPEN.find(d => d.wert === dokumentTyp) ?? DOKUMENT_TYPEN[0];

  /**
   * Laedt genau den Beleg herunter, der gerade angezeigt wird. Bewusst ohne
   * Vorschau-Modal: das Dokument steht bereits auf dem Schirm, und der
   * iframe-Viewer blieb je nach Browser-Einstellung (PDFs immer herunterladen)
   * leer. Den Dateinamen bestimmt der Server.
   */
  const belegHerunterladen = async () => {
    if (!editingAuftrag?.auftrag_id) return;
    setPdfLaedt(true);
    setError('');
    try {
      await downloadDatei(
        `${API_URL}/api/dokumente/auftraege/${editingAuftrag.auftrag_id}/${dokumentTyp}/pdf`,
        `${dokument.kuerzel}_${editingAuftrag.auftrag_nr}.pdf`,
      );
    } catch {
      setError(`${dokument.label} konnte nicht als PDF geladen werden.`);
    } finally {
      setPdfLaedt(false);
    }
  };

  return (
    <div className="auftrag-form-container">
      <div className="auftrag-form-header">
        <h2>{isEdit ? `Auftrag ${editingAuftrag?.auftrag_nr} bearbeiten` : 'Neues Angebot'}</h2>
      </div>

      {isEdit && (
        <div className="auftrag-status-zeile">
          Status: <span className="status-badge">{auftragStatus}</span>
          {!statusBearbeitbar && (
            <span className="hinweis-inline">
              — Bearbeitung nicht möglich, Auftrag hat die Angebotsphase bereits verlassen.
            </span>
          )}
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {isEdit && (
        <div className="ansicht-umschalter">
          <span className="ansicht-umschalter__label">Ansicht</span>
          <div className="ansicht-umschalter__tabs" role="group" aria-label="Ansicht wechseln">
            <button type="button" aria-pressed={ansichtModus === 'maske'}
              className={`ansicht-umschalter__tab${ansichtModus === 'maske' ? ' is-active' : ''}`}
              onClick={() => { setAnsichtModus('maske'); reloadAuftrag(); }}>
              Maske
            </button>
            <button type="button" aria-pressed={ansichtModus === 'dokument'}
              className={`ansicht-umschalter__tab${ansichtModus === 'dokument' ? ' is-active' : ''}`}
              onClick={() => setAnsichtModus('dokument')}>
              Dokument
            </button>
          </div>
          {ansichtModus === 'dokument' && (
            <div className="ansicht-umschalter__doku">
              <select className="ansicht-umschalter__select" value={dokumentTyp} aria-label="Belegtyp"
                onChange={e => setDokumentTyp(e.target.value as DokumentTyp)}>
                {DOKUMENT_TYPEN.map(d => (
                  <option key={d.wert} value={d.wert}>{d.label}</option>
                ))}
              </select>
              <button type="button" className="k-btn k-btn--sm k-btn--ghost"
                title={`${dokument.label} als PDF herunterladen`}
                disabled={pdfLaedt}
                onClick={belegHerunterladen}>
                {pdfLaedt ? '… lädt' : '📄 Download'}
              </button>
            </div>
          )}
        </div>
      )}

      {ansichtModus === 'maske' && (
      <>
      <form onSubmit={isEdit ? handleSubmitKopf : handleSubmitNeu} className="auftrag-form">
        <div className="form-row">
          <div className="form-group">
            <label>Kunde *</label>
            <select
              value={kundeId}
              onChange={e => setKundeId(e.target.value)}
              disabled={isEdit}
              required
            >
              <option value="">-- Kunde wählen --</option>
              {kunden.map(k => (
                <option key={k.partner_id} value={k.partner_id}>{k.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Liefertermin</label>
            <input
              type="date" value={liefertermin} onChange={e => setLiefertermin(e.target.value)}
              disabled={!statusBearbeitbar}
            />
          </div>
          <div className="form-group">
            <label>Priorität (1–10)</label>
            <input
              type="number" min="1" max="10" value={prioritaet}
              onChange={e => setPrioritaet(e.target.value)}
              disabled={!statusBearbeitbar}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Notizen</label>
          <textarea value={notizen} onChange={e => setNotizen(e.target.value)} rows={2} disabled={!statusBearbeitbar} />
        </div>

        {isEdit && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label>Kopftext (Anrede/Einleitung im Beleg)</label>
                <textarea value={kopftext} onChange={e => setKopftext(e.target.value)} rows={2} disabled={!statusBearbeitbar} />
              </div>
              <div className="form-group">
                <label>Fußtext (Grußformel/Hinweise im Beleg)</label>
                <textarea value={fusstext} onChange={e => setFusstext(e.target.value)} rows={2} disabled={!statusBearbeitbar} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Gesamtrabatt (%)</label>
                <input
                  type="number" step="0.01" min="0" max="100" value={gesamtrabatt}
                  onChange={e => setGesamtrabatt(e.target.value)}
                  disabled={!statusBearbeitbar}
                />
              </div>
            </div>
          </>
        )}

        {!isEdit && (
          <div className="positionsgrid-section">
            <h3>Positionen</h3>
            <KatalogSuchePanel onUebernommen={ladeStammdaten} />
            <table className="positionsgrid-table">
              <thead>
                <tr>
                  <th>Artikel</th>
                  <th>Menge</th>
                  <th>Einheit</th>
                  <th>Einzelpreis (EUR)</th>
                  <th>Rabatt %</th>
                  <th>Steuersatz</th>
                  <th>Gesamt</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {neueZeilen.map((z, i) => {
                  const menge = parseFloat(z.menge) || 0;
                  const artikelId = parseInt(z.artikel_id);
                  const preis = z.preis_einzeln
                    ? parseFloat(z.preis_einzeln)
                    : (preisVorschlaege[artikelId] ?? artikelOf(artikelId)?.preis_vk ?? 0);
                  const rabatt = z.rabatt_prozent ? parseFloat(z.rabatt_prozent) : 0;
                  return (
                    <tr key={i}>
                      <td>
                        <select value={z.artikel_id} onChange={e => zeileAendern(i, 'artikel_id', e.target.value)}>
                          <option value="">-- Artikel wählen --</option>
                          {artikelListe.map(a => (
                            <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung} ({a.einheit})</option>
                          ))}
                        </select>
                      </td>
                      <td>
                        <input
                          type="number" step="0.01" min="0.01" className="menge-input"
                          value={z.menge} onChange={e => zeileAendern(i, 'menge', e.target.value)}
                        />
                      </td>
                      <td>{artikelOf(artikelId)?.einheit || '-'}</td>
                      <td>
                        <input
                          type="number" step="0.01" min="0" className="preis-input"
                          placeholder={preisPlatzhalter(z.artikel_id)}
                          value={z.preis_einzeln} onChange={e => zeileAendern(i, 'preis_einzeln', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number" step="0.01" min="0" max="100" className="rabatt-input"
                          value={z.rabatt_prozent} onChange={e => zeileAendern(i, 'rabatt_prozent', e.target.value)}
                        />
                      </td>
                      <td>{steuersatzVon(z.artikel_id)}%</td>
                      <td className="price-cell">
                        {formatPrice(zeilensumme(menge, preis, rabatt))}
                        {(() => {
                          const info = metallInfos[artikelId];
                          const hinweis = zuschlagHinweis(
                            info?.zuschlag, artikelOf(artikelId)?.einheit, info?.notierung_datum,
                          );
                          return hinweis ? <div className="zeile-hinweis">{hinweis}</div> : null;
                        })()}
                      </td>
                      <td>
                        <button type="button" className="btn-delete-small" onClick={() => zeileEntfernen(i)} title="Zeile entfernen">✕</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <button type="button" className="k-btn k-btn--sm" onClick={zeileHinzufuegen}>+ Position hinzufügen</button>
            <SummenVorschau gruppen={summenVorschauNeu()} />
          </div>
        )}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary">Abbrechen</button>
          <button type="submit" disabled={saving || (isEdit && !statusBearbeitbar)} className="btn-primary">
            {saving ? 'Speichere...' : isEdit ? 'Kopf speichern' : 'Angebot anlegen'}
          </button>
        </div>
      </form>

      {isEdit && (
        <div className="positionsgrid-section">
          <h3>Positionen</h3>
          {!statusBearbeitbar && (
            <div className="hinweis-box">Positionen können nur in der Angebotsphase geändert werden.</div>
          )}
          {statusBearbeitbar && <KatalogSuchePanel onUebernommen={ladeStammdaten} />}
          <table className="positionsgrid-table">
            <thead>
              <tr>
                <th>Artikel</th>
                <th>Menge</th>
                <th>Einheit</th>
                <th>Einzelpreis (EUR)</th>
                <th>Rabatt %</th>
                <th>Steuersatz</th>
                <th>Gesamt</th>
                {statusBearbeitbar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {positionen.map(pos => {
                const mengeFehlerKey = `${pos.position_id}-menge`;
                const preisFehlerKey = `${pos.position_id}-preis`;
                const rabattFehlerKey = `${pos.position_id}-rabatt`;
                return (
                <tr key={pos.position_id}>
                  <td>{pos.artikel?.bezeichnung || artikelName(pos.artikel_id)}</td>
                  <td>
                    <input
                      key={`menge-${pos.position_id}-${resetGeneration}`}
                      type="number" step="0.01" min="0.01" className="menge-input"
                      defaultValue={pos.menge}
                      disabled={!statusBearbeitbar || zeileWirdGespeichert === pos.position_id}
                      onBlur={e => {
                        const neueMenge = parseFloat(e.target.value);
                        if (isNaN(neueMenge) || neueMenge <= 0) {
                          zeileFehlerSetzen(mengeFehlerKey, 'Menge muss größer als 0 sein.');
                          setResetGeneration(g => g + 1);
                          return;
                        }
                        zeileFehlerSetzen(mengeFehlerKey, null);
                        if (neueMenge !== pos.menge) {
                          positionAendern(pos, neueMenge, pos.preis_einzeln, pos.rabatt_prozent ?? null);
                        }
                      }}
                    />
                    {zeilenFehler[mengeFehlerKey] && (
                      <div className="zeile-fehler">{zeilenFehler[mengeFehlerKey]}</div>
                    )}
                  </td>
                  <td>{pos.einheit || pos.artikel?.einheit || '-'}</td>
                  <td>
                    <input
                      key={`preis-${pos.position_id}-${resetGeneration}`}
                      type="number" step="0.01" min="0" className="preis-input"
                      defaultValue={pos.preis_einzeln}
                      disabled={!statusBearbeitbar || zeileWirdGespeichert === pos.position_id}
                      onBlur={e => {
                        const neuerPreis = parseFloat(e.target.value);
                        if (isNaN(neuerPreis) || neuerPreis < 0) {
                          zeileFehlerSetzen(preisFehlerKey, 'Preis darf nicht negativ sein.');
                          setResetGeneration(g => g + 1);
                          return;
                        }
                        zeileFehlerSetzen(preisFehlerKey, null);
                        if (neuerPreis !== pos.preis_einzeln) {
                          positionAendern(pos, pos.menge, neuerPreis, pos.rabatt_prozent ?? null);
                        }
                      }}
                    />
                    {zeilenFehler[preisFehlerKey] && (
                      <div className="zeile-fehler">{zeilenFehler[preisFehlerKey]}</div>
                    )}
                  </td>
                  <td>
                    <input
                      key={`rabatt-${pos.position_id}-${resetGeneration}`}
                      type="number" step="0.01" min="0" max="100" className="rabatt-input"
                      defaultValue={pos.rabatt_prozent ?? ''}
                      disabled={!statusBearbeitbar || zeileWirdGespeichert === pos.position_id}
                      onBlur={e => {
                        if (e.target.value === '') {
                          zeileFehlerSetzen(rabattFehlerKey, null);
                          if (pos.rabatt_prozent != null) {
                            positionAendern(pos, pos.menge, pos.preis_einzeln, null);
                          }
                          return;
                        }
                        const wert = parseFloat(e.target.value);
                        if (isNaN(wert) || wert < 0 || wert > 100) {
                          zeileFehlerSetzen(rabattFehlerKey, 'Rabatt muss zwischen 0 und 100 % liegen.');
                          setResetGeneration(g => g + 1);
                          return;
                        }
                        zeileFehlerSetzen(rabattFehlerKey, null);
                        if (wert !== (pos.rabatt_prozent ?? null)) {
                          positionAendern(pos, pos.menge, pos.preis_einzeln, wert);
                        }
                      }}
                    />
                    {zeilenFehler[rabattFehlerKey] && (
                      <div className="zeile-fehler">{zeilenFehler[rabattFehlerKey]}</div>
                    )}
                  </td>
                  <td>{pos.steuersatz ?? 19}%</td>
                  <td className="price-cell">
                    {formatPrice(pos.preis_gesamt)}
                    {(() => {
                      // Snapshot der Position, nicht der Tageskurs: der Beleg
                      // zeigt genau diesen Wert (Spec §8.2).
                      const hinweis = zuschlagHinweis(
                        pos.metallzuschlag_einzeln,
                        pos.einheit || pos.artikel?.einheit,
                        pos.metallzuschlag_notierung_datum,
                      );
                      return hinweis ? <div className="zeile-hinweis">{hinweis}</div> : null;
                    })()}
                  </td>
                  {statusBearbeitbar && (
                    <td>
                      <button
                        type="button" className="btn-delete-small"
                        disabled={zeileWirdGespeichert === pos.position_id}
                        onClick={() => positionLoeschen(pos)}
                        title="Position löschen"
                      >✕</button>
                    </td>
                  )}
                </tr>
                );
              })}
              {positionen.length === 0 && (
                <tr><td colSpan={8} className="empty-hint">Noch keine Positionen vorhanden.</td></tr>
              )}
            </tbody>
          </table>

          {statusBearbeitbar && (
            <div className="add-position-row">
              <select
                value={neuePosition.artikel_id}
                onChange={e => {
                  setNeuePosition(p => ({ ...p, artikel_id: e.target.value }));
                  if (e.target.value) preisVorschlagLaden(parseInt(e.target.value));
                }}
              >
                <option value="">-- Artikel wählen --</option>
                {artikelListe.map(a => (
                  <option key={a.artikel_id} value={a.artikel_id}>{a.bezeichnung} ({a.einheit})</option>
                ))}
              </select>
              <input
                type="number" step="0.01" min="0.01" className="menge-input" placeholder="Menge"
                value={neuePosition.menge} onChange={e => setNeuePosition(p => ({ ...p, menge: e.target.value }))}
              />
              <input
                type="number" step="0.01" min="0" className="preis-input"
                placeholder={preisPlatzhalter(neuePosition.artikel_id)}
                value={neuePosition.preis_einzeln} onChange={e => setNeuePosition(p => ({ ...p, preis_einzeln: e.target.value }))}
              />
              <input
                type="number" step="0.01" min="0" max="100" className="rabatt-input" placeholder="Rabatt %"
                value={neuePosition.rabatt_prozent} onChange={e => setNeuePosition(p => ({ ...p, rabatt_prozent: e.target.value }))}
              />
              <button
                type="button" className="k-btn k-btn--primary k-btn--sm"
                disabled={zeileWirdGespeichert === -1 || !neuePosition.artikel_id}
                onClick={positionHinzufuegen}
              >+ Hinzufügen</button>
            </div>
          )}

          <SummenVorschau gruppen={summenVorschauEdit()} />
        </div>
      )}
      </>
      )}

      {isEdit && ansichtModus === 'dokument' && editingAuftrag && (
        <BelegDokumentView
          ansichtUrl={`${API_URL}/api/auftraege/${editingAuftrag.auftrag_id}/beleg-ansicht?typ=${dokumentTyp}`}
          kontext={{ auftragId: editingAuftrag.auftrag_id }}
          onKonflikt={reloadAuftrag}
        />
      )}

      {editingAuftrag?.auftrag_id != null && regieberichtModulAktiv && (
        <div style={{ marginTop: 24 }}>
          <RegieberichtKarte auftragId={editingAuftrag.auftrag_id} authToken={localStorage.getItem('auth_token') || ''} userRolle={userRolle} />
        </div>
      )}

      {editingAuftrag?.auftrag_id != null && (
        <div style={{ marginTop: 24 }}>
          <AktivitaetenBlock entitaetTyp="auftrag" entitaetId={editingAuftrag.auftrag_id} mitBelegen />
        </div>
      )}
    </div>
  );
};

export default AuftragForm;
